import { persistStateTypes } from 'a/reducers/persistStateReducer';
import { REDUX_ACTIONS, GAME_ID, SCREENS, NO_TAG } from '../../data/constants';

const initState = {
  questionIndex: 0,
  elementsCollected: 0,
  wrongCount: 0,
  hasSeenOnboarding: false,
  hasUntagInteractionBeenTaught: false,
  isUntagLessonPlaying: false,
  isCorrectAnswerUnderlined: false,
  isNotebookShaking: false,
  isDistractorUnderlined: false,
  isOnboardingReplay: false,
  isAutoComplete: false,
  isNotebookPrompt: false,
  currentGuidedSequenceName: '',
  promptText: 'Hey kid, you got any of the basics of this story yet?',
  groupIdForUntag: undefined,
  targetInteractionIds: [],
  blacklistInteractionIds: [],
  currentTagMode: NO_TAG,
};

const onboardingReducer = (state = initState, action) => {
  const { type, payload, gameId } = action;
  if (gameId !== GAME_ID) {
    return state;
  }

  if (type === REDUX_ACTIONS.REPEAT_ONBOARDING) {
    return {
      ...state,
      currentGuidedSequenceName: '',
      isOnboardingReplay: true,
      targetInteractionIds: ['interaction locked'],
    };
  }

  if (state.hasSeenOnboarding && !state.isOnboardingReplay) {
    return state;
  }

  switch (type) {
    case persistStateTypes.REQUESTRESPONSE_GAMESTATE: {
      return {
        ...state,
      };
    }

    case REDUX_ACTIONS.START_OBOARDING: {
      return {
        ...state,
        currentGuidedSequenceName: '',
        targetInteractionIds: ['interaction locked'],
      };
    }

    case REDUX_ACTIONS.CHANGE_SCREEN: {
      if (payload === SCREENS.PROGRESS) {
        return {
          ...state,
          currentGuidedSequenceName: '',
          isOnboardingReplay: false,
        };
      }
      return {
        ...state,
        currentGuidedSequenceName: '',
      };
    }

    case REDUX_ACTIONS.SET_CURRENT_GUIDED_SEQUENCE_NAME:
      return {
        ...state,
        currentGuidedSequenceName: payload,
      };

    case REDUX_ACTIONS.SELECT_TAGGING_MODE: {
      // if target tagging mode is none or is in auto-complete mode (current sequence should be: imGoingToHelp)
      if (payload.text === 'none' || state.isAutoComplete) {
        return state;
      }
      // if there's a groupIdForUntag this is the special case for untag lesson
      if (state.isUntagLessonPlaying) {
        if (payload.text === 'untag') {
          return {
            ...state,
            currentGuidedSequenceName: 'thenSelectTheHighlighted',
            promptText: 'Select the highlighted phrase you want to remove.',
            targetInteractionIds: [state.groupIdForUntag],
          };
        }
        return {
          ...state,
          isCorrectAnswerUnderlined: true,
          currentGuidedSequenceName: 'highlightWhatYouveFound',
          promptText: 'Tap the dotted line.',
          targetInteractionIds: ['correctAnswer'],
          currentTagMode: payload,
        };
      }
      if (state.questionIndex === 0) {
        switch (state.elementsCollected) {
          case 0:
            return {
              ...state,
              currentGuidedSequenceName: 'ahGreatFactFinding',
              isCorrectAnswerUnderlined: true,
              promptText: 'Great! Take notes by highlighting what you find!',
              targetInteractionIds: ['locked'],
            };
          case 1:
            return {
              ...state,
              currentGuidedSequenceName: 'tapOnTheEvidence',
              promptText: 'Tap on the evidence in the text.',
              targetInteractionIds: ['taggableParagraph'],
            };
          case 2: {
            if (payload.text === 'untag') {
              return {
                ...state,
                promptText: 'Select the highlighted phrase you want to remove.',
                targetInteractionIds: [state.groupIdForUntag],
              };
            }
            return {
              ...state,
              currentGuidedSequenceName: 'nowSelectTheEvidence',
              promptText: 'Select the evidence in the text',
              targetInteractionIds: state.wrongCount > 1 ? ['correctAnswer', 'distractors'] : ['taggableParagraph'],
              isDistractorUnderlined: state.wrongCount > 1,
            };
          }
          default:
            return state;
        }
      }
      return {
        ...state,
        currentTagMode: payload,
      };
    }

    case REDUX_ACTIONS.SELECT_WORD_GROUP: {
      const { elementsCollected } = state;
      if (state.isAutoComplete) {
        return state;
      }
      // Cases if we are on Untag Lesson
      if (state.isUntagLessonPlaying) {
        // user has selected the correct evidence
        if (payload.isCorrect) {
          // special case for when it is not first item
          if (state.questionIndex > 0) {
            return {
              ...state,
              promptText: 'Did you find all the facts yet?',
              groupIdForUntag: undefined,
              isCorrectAnswerUnderlined: false,
              targetInteractionIds: [],
              isUntagLessonPlaying: false,
              hasUntagInteractionBeenTaught: true,
              elementsCollected: elementsCollected + 1,
              currentGuidedSequenceName: state.elementsCollected === 0 ? 'finishFindingAllThe' : '',
              blacklistInteractionIds: [...state.blacklistInteractionIds, payload.groupId],
            };
          }
          // we are on first item
          switch (state.elementsCollected) {
            case 1:
              return {
                ...state,
                hasUntagInteractionBeenTaught: true,
                isUntagLessonPlaying: false,
                elementsCollected: elementsCollected + 1,
                currentGuidedSequenceName: 'goodStuffKidWere',
                targetInteractionIds: ['locked'],
                blacklistInteractionIds: [...state.blacklistInteractionIds, payload.groupId],
                promptText: 'Good stuff, kid!',
              };
            case 2:
              return {
                ...state,
                hasUntagInteractionBeenTaught: true,
                isUntagLessonPlaying: false,
                elementsCollected: elementsCollected + 1,
                currentGuidedSequenceName: 'bravoIKnewYoud',
                promptText: 'Bravo, I knew you would be good at this!',
                targetInteractionIds: ['doneButton'],
                blacklistInteractionIds: [...state.blacklistInteractionIds, payload.groupId],
              };
            default:
              return state;
          }
        }
        // user is untagging during the lesson
        if (state.groupIdForUntag === payload.groupId) {
          return {
            ...state,
            targetInteractionIds: ['locked'],
            currentGuidedSequenceName: 'greatNowSelectThe',
            promptText: 'Great! Now select the type of fact to highlight the correct evidence!',
            groupIdForUntag: undefined,
          };
        }
      }
      // if selection is correct (during a regular selection. Not an untag moment)
      if (payload.isCorrect) {
        // it is the first item
        if (state.questionIndex === 0) {
          switch (elementsCollected) {
            case 0:
              return {
                ...state,
                elementsCollected: elementsCollected + 1,
                currentGuidedSequenceName: 'amazingSoNowWe',
                isCorrectAnswerUnderlined: false,
                promptText: 'Amazing!',
                targetInteractionIds: ['locked'],
                blacklistInteractionIds: [...state.blacklistInteractionIds, payload.groupId],
              };
            case 1:
              return {
                ...state,
                elementsCollected: elementsCollected + 1,
                currentGuidedSequenceName: 'goodStuffKidWere',
                targetInteractionIds: ['locked'],
                promptText: 'Good stuff, kid!',
                blacklistInteractionIds: [...state.blacklistInteractionIds, payload.groupId],
              };
            case 2:
              return {
                ...state,
                elementsCollected: elementsCollected + 1,
                currentGuidedSequenceName: 'bravoIKnewYoud',
                promptText: 'Bravo, I knew you would be good at this!',
                targetInteractionIds: ['doneButton'],
              };
            default:
              return state;
          }
        } else if (state.questionIndex === 1 && elementsCollected === 0) {
          return {
            ...state,
            elementsCollected: elementsCollected + 1,
            currentGuidedSequenceName: 'finishFindingAllThe',
          };
        }
      }
      // if selection is incorrect
      if (!payload.isCorrect) {
        // if  we are on first item
        if (state.questionIndex === 0) {
          // user is untagging
          if (state.groupIdForUntag === payload.groupId) {
            return {
              ...state,
              targetInteractionIds: ['taggingPanel'],
              promptText: 'Great! Now select the type of fact to highlight the correct evidence!',
              groupIdForUntag: undefined,
            };
          }
          // if more than 2 wrong, just auto-complete
          if (state.wrongCount >= 2) {
            return {
              ...state,
              isCorrectAnswerUnderlined: false,
              isDistractorUnderlined: false,
              isNotebookShaking: false,
              targetInteractionIds: ['locked'],
              currentGuidedSequenceName: 'imGoingToHelp',
              isAutoComplete: true,
            };
          }
          // user made a mistake, incorrect feedback triggered
          if (!state.groupIdForUntag) {
            return {
              ...state,
              isUntagLessonPlaying: !state.hasUntagInteractionBeenTaught,
              currentGuidedSequenceName: 'youSureAboutThat',
              groupIdForUntag: payload.groupId,
              promptText: 'You sure about that?',
              targetInteractionIds: ['locked'],
              wrongCount: state.wrongCount + 1,
            };
          }
        }
        // if we are on the second item
        if (state.questionIndex === 1) {
          // trigger untag lesson
          if (!state.hasUntagInteractionBeenTaught && state.currentTagMode.text !== 'untag') {
            return {
              ...state,
              isUntagLessonPlaying: true,
              currentGuidedSequenceName: 'youSureAboutThat',
              groupIdForUntag: payload.groupId,
              promptText: 'You sure about that?',
              targetInteractionIds: ['locked'],
              wrongCount: state.wrongCount + 1,
            };
          }
          // user is untagging incorrect evidence
          if (state.groupIdForUntag === payload.groupId) {
            return {
              ...state,
              targetInteractionIds: ['taggingPanel'],
              promptText: 'Great! Now select the type of fact to highlight the correct evidence!',
              groupIdForUntag: undefined,
            };
          }
          // if it's incorrect, is the first tag of second item and untag has alreay been taught
          if (state.elementsCollected === 0 && state.currentTagMode.text !== 'untag') {
            return {
              ...state,
              elementsCollected: elementsCollected + 1,
              currentGuidedSequenceName: 'youAreGettingA',
            };
          }
        }
      }
      return state;
    }

    case REDUX_ACTIONS.END_OF_GUIDED_SEQUENCE:
      switch (payload) {
        case 'heyKidYouGot_onboarding':
          return {
            ...state,
            targetInteractionIds: ['taggingPanel'],
            currentGuidedSequenceName: '',
          };

        case 'oneMoreThingBefore':
          return {
            ...state,
            isNotebookShaking: true,
            isNotebookPrompt: true,
            targetInteractionIds: ['notebook'],
            currentGuidedSequenceName: '',
          };

        case 'ahGreatFactFinding':
          return {
            ...state,
            targetInteractionIds: ['taggableParagraph'],
            currentGuidedSequenceName: '',
          };

        case 'amazingSoNowWe': {
          if (state.questionIndex === 0) {
            return {
              ...state,
              targetInteractionIds: ['taggingPanel'],
              promptText: 'What other facts can you find?',
              currentGuidedSequenceName: '',
            };
          }
          return {
            ...state,
            targetInteractionIds: [],
            currentGuidedSequenceName: '',
          };
        }

        case 'youSureAboutThat': {
          if (!state.hasUntagInteractionBeenTaught) {
            return {
              ...state,
              currentGuidedSequenceName: 'removeTheHighlightBy',
            };
          }
          return {
            ...state,
            targetInteractionIds: ['untagOption'],
            currentGuidedSequenceName: '',
          };
        }

        case 'removeTheHighlightBy':
          return {
            ...state,
            promptText: 'Select the untag button.',
            currentGuidedSequenceName: '',
          };

        case 'goodStuffKidWere':
          return {
            ...state,
            groupIdForUntag: undefined,
            isCorrectAnswerUnderlined: false,
            isDistractorUnderlined: false,
            promptText: state.elementsCollected === 2 ?
              'Did you find the last fact yet?' : 'Did you find another fact yet?',
            targetInteractionIds: state.questionIndex === 0 ? ['taggingPanel'] : [],
            currentGuidedSequenceName: '',
          };

        case 'imGoingToHelp':
          return {
            ...state,
            promptText: 'Let me help.',
            elementsCollected: state.elementsCollected + 1,
            targetInteractionIds: ['locked'],
            currentGuidedSequenceName: '',
          };

        case 'finishFindingAllThe':
          return {
            ...state,
            targetInteractionIds: [],
            isNotebookShaking: false,
            isNotebookPrompt: false,
            currentGuidedSequenceName: '',
          };

        default:
          return { ...state, currentGuidedSequenceName: '' };
      }

    case REDUX_ACTIONS.TOGGLE_DIARY_PANEL: {
      if (state.questionIndex === 1 && state.isNotebookPrompt) {
        if (state.isNotebookShaking) {
          return {
            ...state,
            currentGuidedSequenceName: 'nowLetsGetBack',
            isNotebookShaking: false,
          };
        }
        return {
          ...state,
          targetInteractionIds: [],
          isNotebookPrompt: false,
          currentGuidedSequenceName: '',
        };
      }
      return {
        ...state,
        currentGuidedSequenceName: '',
      };
    }

    case REDUX_ACTIONS.SET_TARGET_INTERACTION_WITH_ID:
      return {
        ...state,
        targetInteractionIds: [payload],
      };

    case REDUX_ACTIONS.ADD_ID_TO_INTERACTION_BLACKLIST:
      return {
        ...state,
        blacklistInteractionIds: [...state.blacklistInteractionIds, payload],
      };

    case REDUX_ACTIONS.NEXT_STANDARD_ITEM:
      return {
        ...state,
        promptText: 'Hey kid, you got any of the basics of this story yet?',
        isNotebookPrompt: true,
        isCorrectAnswerUnderlined: false,
        isDistractorUnderlined: false,
        targetInteractionIds: state.questionIndex === 0 ? ['locked'] : [],
        blacklistInteractionIds: [],
        elementsCollected: 0,
        questionIndex: state.questionIndex + 1,
        wrongCount: 0,
        isAutoComplete: false,
      };

    case REDUX_ACTIONS.SELECT_RESPONSE: {
      if (state.questionIndex === 0) {
        return {
          ...state,
          currentGuidedSequenceName: 'youGotTheStory',
          targetInteractionIds: ['locked'],
        };
      } if (state.questionIndex === 1 && payload.isCorrect) {
        return {
          ...state,
          currentGuidedSequenceName: 'heresYourChanceTo',
        };
      }
      return state;
    }

    case REDUX_ACTIONS.SET_LEVEL_ID:
      return {
        ...initState,
        hasSeenOnboarding: state.hasSeenOnboarding,
      };

    case REDUX_ACTIONS.COMPLETE_ONBOARDING:
      return {
        ...initState,
        isOnboardingReplay: false,
        hasSeenOnboarding: true,
      };

    case REDUX_ACTIONS.SELECT_HEADLINE_TEXT:
    case REDUX_ACTIONS.STOP_VO:
      return {
        ...state,
        currentGuidedSequenceName: '',
      };

    default:
      return state;
  }
};

export default onboardingReducer;
